/*
 Asteria Elite Birth Chart Engine V1
 Planetary calculations: Astronomy Engine by Don Cross, MIT License.
 House cusp implementation: classical iterative Placidus equations; validation informed by
 tri-horoscope by Kosuke Nakata, MIT License.
 Technical attribution only; engine/library names are not customer-facing.
*/
(() => {
  'use strict';

  const D = Math.PI / 180;
  const R = 180 / Math.PI;
  const HOUR = 3600000;
  const BODIES = ['Sun','Moon','Mercury','Venus','Mars','Jupiter','Saturn','Uranus','Neptune','Pluto'];
  const PERSONAL = new Set(['Sun','Moon','Mercury','Venus','Mars']);
  const WEIGHTS = Object.freeze({Sun:4,Moon:4,Rising:4,ChartRuler:3,Mercury:2,Venus:2,Mars:2,Jupiter:1,Saturn:1,Uranus:.5,Neptune:.5,Pluto:.5});
  const RULERS = Object.freeze(['Mars','Venus','Mercury','Moon','Sun','Mercury','Venus','Pluto','Jupiter','Saturn','Uranus','Neptune']);
  const TRADITIONAL_RULERS = Object.freeze({7:'Mars',10:'Saturn',11:'Jupiter'});
  const ASPECTS = Object.freeze([
    {key:'conjunction',angle:0,tr:'Kavuşum',en:'Conjunction'},
    {key:'sextile',angle:60,tr:'Sekstil',en:'Sextile'},
    {key:'square',angle:90,tr:'Kare',en:'Square'},
    {key:'trine',angle:120,tr:'Üçgen',en:'Trine'},
    {key:'opposition',angle:180,tr:'Karşıt',en:'Opposition'}
  ]);

  const norm = v => ((Number(v) % 360) + 360) % 360;
  const circularDiff = (a,b) => Math.abs(norm(a-b+180)-180);
  const signedDiff = (a,b) => norm(a-b+180)-180;
  const signIndex = lon => Math.floor(norm(lon)/30);
  const degInSign = lon => norm(lon)%30;

  function astronomy() {
    return globalThis.Astronomy || null;
  }

  function sharedTime() {
    return globalThis.AsteriaAstroEngine || null;
  }

  function meanObliquity(t) {
    const T = t.tt / 36525;
    return 23.43929111 - (46.8150*T + 0.00059*T*T - 0.001813*T*T*T)/3600;
  }

  function lonFromRa(ra, eps) {
    const r=ra*D, e=eps*D;
    return norm(Math.atan2(Math.sin(r), Math.cos(r)*Math.cos(e))*R);
  }

  function angles(ramc, eps, lat) {
    const r=ramc*D, e=eps*D, p=Math.max(-89.999,Math.min(89.999,Number(lat)))*D;
    return {
      mc:norm(Math.atan2(Math.sin(r),Math.cos(r)*Math.cos(e))*R),
      asc:norm(Math.atan2(Math.cos(r),-(Math.sin(r)*Math.cos(e)+Math.tan(p)*Math.sin(e)))*R)
    };
  }

  function placidus(ramc, eps, lat) {
    function solve(frac, night) {
      const base=ramc+(night?180:0);
      let ra=norm(base+(night?-1:1)*frac*90);
      for(let i=0;i<300;i++) {
        const lam=lonFromRa(ra,eps);
        const dec=Math.asin(Math.sin(eps*D)*Math.sin(lam*D))*R;
        const raw=Math.tan(Number(lat)*D)*Math.tan(dec*D);
        if(!Number.isFinite(raw) || Math.abs(raw)>1) return null;
        const ad=Math.asin(raw)*R;
        const arc=night ? 90-ad : 90+ad;
        const next=norm(base+(night?-1:1)*frac*arc);
        if(circularDiff(next,ra)<1e-10){ra=next;break;}
        ra=next;
      }
      return lonFromRa(ra,eps);
    }
    const a=angles(ramc,eps,lat);
    const c11=solve(1/3,false), c12=solve(2/3,false), c2=solve(2/3,true), c3=solve(1/3,true);
    if([c11,c12,c2,c3].some(v=>v==null)) return null;
    const c=Array(13).fill(null);
    c[1]=a.asc;c[10]=a.mc;c[11]=c11;c[12]=c12;c[2]=c2;c[3]=c3;
    for(const i of [1,2,3,10,11,12]) c[(i+5)%12+1]=norm(c[i]+180);
    return c;
  }

  function forwardThirds(start,end) {
    const span=norm(end-start);
    return [norm(start+span/3),norm(start+2*span/3)];
  }

  function porphyry(ramc, eps, lat) {
    const a=angles(ramc,eps,lat);
    const c=Array(13).fill(null);
    c[1]=a.asc;c[7]=norm(a.asc+180);c[10]=a.mc;c[4]=norm(a.mc+180);
    [c[2],c[3]]=forwardThirds(c[1],c[4]);
    [c[5],c[6]]=forwardThirds(c[4],c[7]);
    [c[8],c[9]]=forwardThirds(c[7],c[10]);
    [c[11],c[12]]=forwardThirds(c[10],c[1]);
    return c;
  }

  function houseOf(lon,cusps) {
    for(let h=1;h<=12;h++) {
      const a=cusps[h], b=cusps[h%12+1], span=norm(b-a);
      if(norm(lon-a)<span) return h;
    }
    return 1;
  }

  function eclipticLongitude(body,t) {
    const A=astronomy();
    if(!A) return NaN;
    if(body==='Moon') return norm(A.EclipticGeoMoon(t).lon);
    return norm(A.Ecliptic(A.GeoVector(A.Body[body],t,true)).elon);
  }

  function motion(body,ms) {
    const A=astronomy();
    const before=eclipticLongitude(body,A.MakeTime(new Date(ms-6*HOUR)));
    const after=eclipticLongitude(body,A.MakeTime(new Date(ms+6*HOUR)));
    return {retrograde:signedDiff(after,before)<0};
  }

  function maxOrb(aspect,a,b) {
    const lum=a==='Sun'||a==='Moon'||b==='Sun'||b==='Moon';
    if(aspect.key==='sextile') return lum?6:5;
    return lum?8:6;
  }

  function aspectStrength(orb) {
    return orb<=1.5?'very_tight':orb<=3?'tight':'standard';
  }

  function planetAspects(longitudes) {
    const out=[];
    for(let i=0;i<BODIES.length;i++) {
      for(let j=i+1;j<BODIES.length;j++) {
        const a=BODIES[i], b=BODIES[j], sep=circularDiff(longitudes[a],longitudes[b]);
        let best=null;
        for(const asp of ASPECTS) {
          const orb=Math.abs(sep-asp.angle), max=maxOrb(asp,a,b);
          if(orb<=max && (!best || orb<best.orb)) best={...asp,orb,maxOrb:max};
        }
        if(best) out.push({a,b,key:best.key,angle:best.angle,orb:best.orb,strength:aspectStrength(best.orb),tr:best.tr,en:best.en});
      }
    }
    return out.sort((x,y)=>x.orb-y.orb);
  }

  function angleAspects(longitudes,asc,mc) {
    const out=[];
    for(const body of BODIES) {
      for(const [point,lon] of [['Asc',asc],['MC',mc]]) {
        const orb=Math.min(circularDiff(longitudes[body],lon),circularDiff(longitudes[body],norm(lon+180)));
        if(orb<=4) out.push({body,point,orb,strength:aspectStrength(orb)});
      }
    }
    return out.sort((a,b)=>a.orb-b.orb);
  }

  function computeInstant(ms, place, withHouses=true) {
    const A=astronomy();
    if(!A) return {ok:false,reason:'astronomy_engine_missing'};
    const t=A.MakeTime(new Date(ms));
    const longitudes={};
    const motions={};
    for(const body of BODIES) {
      longitudes[body]=eclipticLongitude(body,t);
      motions[body]=motion(body,ms);
    }
    const result={
      ok:true,utcMs:ms,longitudes,motions,
      signs:Object.fromEntries(BODIES.map(b=>[b,signIndex(longitudes[b])])),
      degrees:Object.fromEntries(BODIES.map(b=>[b,degInSign(longitudes[b])])),
      majorAspects:planetAspects(longitudes)
    };
    if(!withHouses) return result;

    const eps=meanObliquity(t);
    const ramc=norm((A.SiderealTime(t)+Number(place.longitude)/15)*15);
    const a=angles(ramc,eps,place.latitude);
    let cusps=placidus(ramc,eps,place.latitude);
    let houseSystem='placidus';
    if(!cusps) {
      cusps=porphyry(ramc,eps,place.latitude);
      houseSystem='porphyry_fallback';
    }
    const houses=Object.fromEntries(BODIES.map(b=>[b,houseOf(longitudes[b],cusps)]));
    const rising=signIndex(a.asc), mc=signIndex(a.mc);
    return {
      ...result,eps,ramc,ascendant:a.asc,mcLongitude:a.mc,risingSign:rising,mcSign:mc,
      chartRuler:RULERS[rising],
      traditionalChartRuler:TRADITIONAL_RULERS[rising]||null,
      cusps,houses,houseSystem,angleAspects:angleAspects(longitudes,a.asc,a.mc)
    };
  }

  function resolveExact(date,time,place) {
    const T=sharedTime();
    if(!T?.localToUtc) return {ok:false,reason:'timezone_engine_missing'};
    const u=T.localToUtc(date,time,place?.timezone);
    if(!u.ok) return u;
    const c=computeInstant(u.ms,place,true);
    return c.ok?{...c,mode:'exact',offsetMinutes:u.offsetMinutes}:c;
  }

  function shiftMs(ms,minutes){return ms+minutes*60000;}

  function resolveApproximate(date,time,place) {
    const T=sharedTime();
    if(!T?.localToUtc) return {ok:false,reason:'timezone_engine_missing'};
    const u=T.localToUtc(date,time,place?.timezone);
    if(!u.ok) return u;
    const minus=computeInstant(shiftMs(u.ms,-30),place,true);
    const center=computeInstant(u.ms,place,true);
    const plus=computeInstant(shiftMs(u.ms,30),place,true);
    if(!minus.ok||!center.ok||!plus.ok) return {ok:false,reason:'calculation_failed'};
    const risingStable=minus.risingSign===center.risingSign&&center.risingSign===plus.risingSign;
    const mcStable=minus.mcSign===center.mcSign&&center.mcSign===plus.mcSign;
    const personalHousesStable=['Sun','Moon','Mercury','Venus','Mars'].every(b=>minus.houses[b]===center.houses[b]&&center.houses[b]===plus.houses[b]);
    return {...center,mode:'approximate',offsetMinutes:u.offsetMinutes,sensitivity:{
      risingStable,mcStable,personalHousesStable,
      timeSensitive:!(risingStable&&mcStable&&personalHousesStable)
    }};
  }

  function localCandidates(date,time,timezone) {
    const T=sharedTime();
    if(!T?.localToUtc) return null;
    return T.localToUtc(date,time,timezone);
  }

  function resolveUnknown(date,place) {
    const samples=[];
    for(const time of ['00:30','06:00','12:00','18:00','23:30']) {
      const u=localCandidates(date,time,place?.timezone);
      if(u?.ok) samples.push(computeInstant(u.ms,place,false));
    }
    if(samples.length<3 || samples.some(x=>!x.ok)) return {ok:false,reason:'civil_day_unresolved'};
    const stableSigns={};
    for(const body of BODIES) stableSigns[body]=samples.every(x=>x.signs[body]===samples[0].signs[body])?samples[0].signs[body]:null;
    const aspectKey=x=>[x.a,x.b,x.key].join('|');
    const sets=samples.map(s=>new Set(s.majorAspects.map(aspectKey)));
    const stableAspects=samples[0].majorAspects.filter(a=>sets.every(set=>set.has(aspectKey(a))));
    return {
      ok:true,mode:'unknown',timeUnknown:true,
      stableSigns,
      representative:samples[Math.floor(samples.length/2)],
      majorAspects:stableAspects,
      ascendant:null,mcLongitude:null,risingSign:null,mcSign:null,chartRuler:null,
      cusps:null,houses:null,houseSystem:null
    };
  }

  function compute(input) {
    const {date,time,timeStatus,place}=input||{};
    if(!date||!place?.timezone||!Number.isFinite(Number(place.latitude))||!Number.isFinite(Number(place.longitude))) return {ok:false,reason:'invalid_input'};
    if(timeStatus==='unknown') return resolveUnknown(date,place);
    if(!time) return {ok:false,reason:'time_required'};
    if(timeStatus==='approximate') return resolveApproximate(date,time,place);
    return resolveExact(date,time,place);
  }

  globalThis.AsteriaBirthChartEngine=Object.freeze({
    compute,computeInstant,resolveExact,resolveApproximate,resolveUnknown,
    placidus,porphyry,houseOf,signIndex,degInSign,circularDiff,
    bodies:[...BODIES],weights:WEIGHTS,aspects:ASPECTS
  });
})();